import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Avatar } from '@progress/kendo-react-layout';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import BarLoader from 'react-spinners/BarLoader';
import { Grid, GridColumn as Column, GridNoRecords } from '@progress/kendo-react-grid';
import { palette } from '../../../system/styles/styles';
import database, { secondDatabase } from '../../../model/firebase/firebase';
import DownloadReport from '../excel/report';

const LoadUserApplications = ({ open, user, handleModal, className }) => {
  const { platform } = useSelector((state) => state);

  const [state, setState] = useState({
    loading: true,
    applications: [],
  });

  const prepareUserApplications = async (id) => {
    const total = [];
    const {docs} = await database
        .collection(platform.platformName)
        .doc('data')
        .collection('users')
        .doc(id)
        .collection('history')
        .get();
    for await (const app of docs) {
      const data = await loadApplicantData(app, total, id);
      if (data) {
        total.push(data);
      }
    }

    setState((prevState) => ({
      ...prevState,
      loading: false,
      applications: total,
    }));
  };

  const loadApplicantData = async (doc) => {
    const data = doc.data();
    const applicationID = data.objectID ? data.objectID : doc.id;
    if (data.ATS) {
      const getter = await secondDatabase
        .collection('companies')
        .doc(data.vacancyOwner)
        .collection('jobs')
        .doc(data.jobID)
        .collection('organicCanidate')
        .doc(applicationID)
        .get();
      if (getter.exists) {
        const application = getter.data();
        return {
          ...application,
          ...data,
        };
      }
    } else {
      const getter = await database
        .collection(platform.platformName)
        .doc('data')
        .collection('companies')
        .doc(data.vacancyOwner)
        .collection('jobs')
        .doc(data.jobID)
        .collection('applicants')
        .doc(applicationID)
        .get();
      if (getter.exists) {
        const application = getter.data();
        return {
          ...application,
          ...data,
        };
      }
    }
  };

  const loadingCell = (tdElement, others) => {
    const { dataItem, field } = others;

    if (field === 'time') {
      return <td>{dayjs(dataItem.time).format('DD / MMM / YYYY')}</td>;
    }

    if (field === 'process') {
      return <td>{dataItem.process ? dataItem.process : 'Pendiente'}</td>;
    }

    return tdElement;
  };

  useEffect(() => {
    if (user.id) {
      prepareUserApplications(user.id);
    } else {
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));
    }
  }, [user]);

  if (!open) return null;

  return (
    <Dialog title="Detalles de postulaciones" onClose={() => handleModal(false, '')} className={className}>
      <div>
        {state.loading ? (
          <BarLoader sizeUnit="px" size={750} color={palette.blue} />
        ) : (
          <>
            <div className="userInfoContainer">
              <div className="userInfoBlock">
                <Avatar
                  shape="circle"
                  type="text"
                  style={{
                    marginRight: 5,
                  }}>
                  {user.names ? `${user.names.substring(0, 1)} ${user.lastNames.substring(0, 1)}` : 'UNK'}
                </Avatar>
                <p>
                  Nombre:
                  <span>{user.names ? user.names : ''}</span>
                </p>
                <p>
                  Apellidos:
                  <span>{user.lastNames ? user.lastNames : ''}</span>
                </p>
                <p>
                  {platform.idTypeText}:<span>{user.scholarID ? user.scholarID : 'No definido'}</span>
                </p>
                {platform.platformCampus.length > 0 ? (
                  <p>
                    Campus/ Sede / Región:
                    <span>{user.campusSegment ? user.campusSegment : 'No definido'}</span>
                  </p>
                ) : null}
              </div>
              <div className="userInfoBlock">
                <p>
                  Teléfono:
                  <span>{user.phoneNumber ? user.phoneNumber : 'No definido'}</span>
                </p>
                <p>
                  Email:
                  <span>{user.email ? user.email : 'No definido'}</span>
                </p>
                <p>
                  Carrera o curso:
                  <span>{user.career ? user.career : 'No definido'}</span>
                </p>
                <p>
                  Estatus:
                  <span>
                    {user.userStatus === 'active'
                      ? 'En búsqueda activa'
                      : user.userStatus === 'openTo'
                      ? 'Abierto a oportunidades'
                      : user.userStatus === 'disabled'
                      ? 'No disponible'
                      : 'En búsqueda activa'}
                  </span>
                </p>
                <p>
                  Total de postulaciones
                  <span>{state.applications.length}</span>
                </p>
              </div>
              {state.applications.length > 0 ? (
                <div className="downloadContainer">
                  <DownloadReport
                    applications={state.applications}
                    filename={`Reporte-${user.names}-${user.lastNames}`}
                    className="exportExcel"
                  />
                </div>
              ) : null}
            </div>
            <div className="subContainer">
              <Grid
                style={{
                  height: '100%',
                }}
                data={state.applications}
                cellRender={loadingCell}>
                <Column field="jobName" title="Vacante" />
                <Column field="companyName" title="Empresa" />
                <Column field="locationName" title="Ubicación" />
                <Column field="jobPay" title="Oferta" />
                <Column field="time" title="Fecha" />
                <Column field="process" title="Estatus" />
                <GridNoRecords>Este estudiante no se ha postulado a ninguna vacante</GridNoRecords>
              </Grid>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default styled(LoadUserApplications)`
  .k-dialog-titlebar {
    background-color: #0f3f44 !important;
  }
  .userInfoContainer {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    position: relative;
  }
  .userInfoBlock {
    width: 50%;
    padding: 30px 50px;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: flex-start;
    p {
      font-size: 1.8rem;
      font-weight: 500;
      color: ${palette.black};
      margin: 7px 0px;
      span {
        font-size: 1.8rem;
        font-weight: 700;
        color: ${palette.black};
        margin: 7px;
      }
    }
  }
  .subContainer {
    width: 100%;
    margin: 25px auto;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${palette.white};
    border-radius: 4px;
    flex-direction: column;
    height: 50%;
  }
  .downloadContainer {
    width: 100%;
    padding: 10px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    position: absolute;
    top: 90%;
    right: 40px;
  }
  .exportExcel {
    padding: 8px 28px;
    font-size: 1.4rem;
    background-color: var(--button-color);
    color: ${palette.white};
  }
`;
